"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Field, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps } from "./types";

export interface DateFieldProps extends FieldBindingProps<Date | null> {
  label?: string;
  placeholder?: string;
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
  disabled?: boolean;
  isRequired?: boolean;
  fromDate?: Date;
  toDate?: Date;
}

export function DateField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  placeholder = "Select date",
  captionLayout,
  disabled,
  fromDate,
  toDate,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <FieldDatePicker
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        open={open}
        setOpen={setOpen}
        placeholder={placeholder}
        captionLayout={captionLayout}
        disabled={disabled}
        fromDate={fromDate}
        toDate={toDate}
        hasLabel={!!label}
      />
      <FieldError />
    </Field>
  );
}

function FieldDatePicker({
  value,
  onChange,
  onBlur,
  open,
  setOpen,
  placeholder,
  captionLayout,
  disabled,
  fromDate,
  toDate,
  hasLabel,
}: {
  value: Date | null;
  onChange: (value: Date | null) => void;
  onBlur: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  placeholder: string;
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years";
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  hasLabel: boolean;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(o) => {
        if (!disabled) {
          setOpen(o);
          if (!o) onBlur();
        }
      }}
    >
      <PopoverTrigger
        render={
          <Button
            id={id}
            variant="outline"
            className={`w-full justify-start font-normal ${hasLabel ? "mt-1" : ""}`}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${id}-error` : undefined}
          >
            {value ? value.toLocaleDateString() : placeholder}
          </Button>
        }
      />
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          defaultMonth={value ?? fromDate ?? undefined}
          captionLayout={captionLayout}
          endMonth={toDate}
          disabled={[...(fromDate ? [{ before: fromDate }] : []), ...(toDate ? [{ after: toDate }] : [])]}
          onSelect={(date) => {
            onChange(date ?? null);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
